import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useId, useRef, useState } from "react";
import { FileTable, type Sort } from "../components/FileTable.tsx";
import styles from "../components/FolderView.module.css";
import { GooglyEyes } from "../components/GooglyEyes.tsx";
import { Icon } from "../components/Icon.tsx";
import { StatePanel } from "../components/StatePanel.tsx";
import ui from "../components/ui.module.css";
import { formatCount } from "../lib/format.ts";
import { messageOf, searchQuery } from "../lib/queries.ts";
import search from "./search.module.css";

export const Route = createFileRoute("/search")({
  validateSearch: (raw: Record<string, unknown>): { q: string } => ({
    q: typeof raw.q === "string" ? raw.q.slice(0, 200) : "",
  }),
  loaderDeps: ({ search: { q } }) => ({ q: q.trim() }),
  loader: ({ context, deps: { q } }) =>
    q === "" ? null : context.queryClient.ensureQueryData(searchQuery(q)),
  component: SearchPage,
  errorComponent: SearchError,
});

function SearchPage() {
  const { q } = Route.useSearch();
  const term = q.trim();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(q);
  const [sort, setSort] = useState<Sort | null>(null);
  const fieldId = useId();
  const field = useRef<HTMLInputElement>(null);
  const results = useQuery({ ...searchQuery(term), enabled: term !== "" });

  useEffect(() => setDraft(q), [q]);

  useEffect(() => {
    if (term === "") field.current?.focus();
  }, [term]);

  useEffect(() => {
    document.title = term ? `${term} · Search · Vault` : "Search · Vault";
  }, [term]);

  const items = results.data?.items ?? [];

  return (
    <div className={styles.page}>
      <div className={styles.top}>
        <div className={styles.intro}>
          <div className={styles.heading}>
            <h1 className={styles.title}>Search</h1>
          </div>
        </div>
        <form
          className={search.form}
          onSubmit={(event) => {
            event.preventDefault();
            void navigate({ to: "/search", search: { q: draft.trim() } });
          }}
        >
          <div className={styles.search}>
            <label htmlFor={fieldId} className={ui.visuallyHidden}>
              Search files
            </label>
            <Icon name="search" className={styles.searchIcon} />
            <input
              ref={field}
              id={fieldId}
              type="search"
              className={`${ui.input} ${styles.searchInput}`}
              value={draft}
              placeholder="Search files — mix, invoice.pdf"
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => setDraft(event.target.value)}
            />
          </div>
          <button type="submit" className={`${ui.button} ${search.submit}`} data-variant="primary">
            Search
          </button>
          <p className={search.count} aria-live="polite">
            {term && items.length > 0 ? formatCount(items.length, "match", "matches") : ""}
          </p>
        </form>
      </div>

      {term === "" ? (
        <div className={styles.folderEmpty}>
          <GooglyEyes />
          <p className={styles.folderEmptyTitle}>Search the whole drive</p>
          <p className={styles.noMatchDetail}>
            Type part of a name. Folders and files both show up, with where they sit.
          </p>
        </div>
      ) : results.isPending ? (
        <div className={styles.pendingList} aria-busy="true">
          {Array.from({ length: 4 }, (_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static placeholders
            <div key={index} className={styles.pendingRow}>
              <span className={ui.skeleton} />
              <span className={ui.skeleton} />
            </div>
          ))}
        </div>
      ) : results.isError ? (
        <StatePanel icon="alert" title="Search did not run" detail={messageOf(results.error)}>
          <button type="button" className={ui.button} onClick={() => void results.refetch()}>
            <Icon name="retry" />
            Try again
          </button>
        </StatePanel>
      ) : items.length === 0 ? (
        <div className={styles.noMatch}>
          <GooglyEyes />
          <p className={styles.noMatchTitle}>Nothing named “{term}”</p>
          <p className={styles.noMatchDetail}>Names only, and the start of a word counts.</p>
        </div>
      ) : (
        <FileTable nodes={items} sort={sort} onSort={setSort} showLocation />
      )}
    </div>
  );
}

function SearchError({ error, reset }: { error: unknown; reset: () => void }) {
  const router = useRouter();
  return (
    <StatePanel icon="alert" title="Search did not run" detail={messageOf(error)}>
      <button
        type="button"
        className={ui.button}
        onClick={() => {
          reset();
          void router.invalidate();
        }}
      >
        <Icon name="retry" />
        Try again
      </button>
    </StatePanel>
  );
}
